import { useEffect, useState, useMemo } from "react";

import ElementaryProgressReportDao from "../dao/ElementaryProgressReportDao";

import { normalizeRating, stripHtml } from "../utils/teacher/InputRatingUtils";
import { gradeTranslation } from "../utils/SchoolAdmin/translations";
import { gradeNormalizer } from "../utils/GradeNormalizer";

// Helper: Extract content items from a GLE (including the GLE itself and its EOs/EOCs)
const extractContentItems = (gle, standard) => {
    const items = [];

    // Include the GLE itself if it is reportable, using the same label-stripping
    // logic as buildGleItem in StudentPageUtils so the body-text lookup key matches.
    if (gle.reportFlag) {
        items.push({
            templateContentId: gle.templateContentId,
            contentType: "GLE",
            body: gle.gleName.replace(/^\d+\.\s*/, ""),
            standardName: standard.standardName,
            gleName: gle.gleName
        });
    }

    for (const eo of gle.evidenceOutcomes || []) {
        if (eo.reportFlag) {
            items.push({
                templateContentId: eo.templateContentId,
                contentType: "EVIDENCE_OUTCOME",
                body: eo.body,
                standardName: standard.standardName,
                gleName: gle.gleName
            });
        }

        for (const child of eo.evidenceOutcomeChildList || []) {
            if (child.reportFlag) {
                items.push({
                    templateContentId: child.templateContentId,
                    contentType: "EVIDENCE_OUTCOME_CHILD",
                    body: child.body,
                    standardName: standard.standardName,
                    gleName: gle.gleName,
                    eoName: eo.body
                });
            }
        }
    }

    return items;
};

// Helper: Build content items from template
const buildContentItemsFromTemplate = (template) => {
    const items = [];
    const standards = template.cdeStandards || [];

    for (const standard of standards) {
        for (const gle of standard.gles || []) {
            items.push(...extractContentItems(gle, standard));
        }
    }

    return items;
};

// Helper: Check if profile matches semester and contains student
const profileMatchesStudentInSemester = (profile, semesterKey, studentNumber) => {
    const matchesTimePeriod = Number(profile.timePeriod) === Number(semesterKey);
    if (!matchesTimePeriod) return false;

    return profile.gradingProfileStudents?.some((s) => String(s.studentNumber) === String(studentNumber));
};

// Helper: Find semester 1 template by subject name
const findSemester1Template = (schoolLevelTemplateDto, previousSemesterProfile, selectedTemplate) => {
    if (!schoolLevelTemplateDto || !selectedTemplate || !previousSemesterProfile) return null;

    const firstSemesterTemplates = schoolLevelTemplateDto.firstSemester?.gradeToSubjects || [];

    const gradeTemplates = firstSemesterTemplates.find((g) => {
        // Normalize both grades for comparison
        const templateGrade = gradeNormalizer(gradeTranslation[g.gradeLevel] || g.gradeLevel);
        const profileGrade = gradeNormalizer(previousSemesterProfile.gradeName);

        return templateGrade === profileGrade || g.profileGradeId === previousSemesterProfile.grade;
    });

    if (!gradeTemplates) return null;

    return gradeTemplates.subjectToStandards?.find((s) => s.subjectName === selectedTemplate.subjectName);
};

/**
 * Hook to fetch previous semester ratings for a student
 * Used to show semester 1 ratings when viewing semester 2
 * Works for both homeroom and shared students
 */
const usePreviousSemesterRatings = ({
    student,
    selectedTemplate,
    currentTimePeriodDto,
    timePeriodDto,
    gradingProfiles,
    schoolLevelTemplateDto,
    schoolYearDto,
    sharedGroup,
    token,
    schoolTemplatesByLocation
}) => {
    const [previousSemesterRatings, setPreviousSemesterRatings] = useState(null);
    const [previousSemesterContentItems, setPreviousSemesterContentItems] = useState([]);
    const [previousSemesterSleRatings, setPreviousSemesterSleRatings] = useState([]);
    const [previousSemesterProfile, setPreviousSemesterProfile] = useState(null);

    const isSecondSemester = currentTimePeriodDto?.name === "2nd Semester";

    // Find or fetch the previous semester's grading profile for this student
    useEffect(() => {
        if (!isSecondSemester || !student || !timePeriodDto || !schoolYearDto || !token) {
            setPreviousSemesterProfile(null);
            return;
        }

        const firstSemester = timePeriodDto.find((tp) => tp.name === "1st Semester");
        if (!firstSemester) {
            setPreviousSemesterProfile(null);
            return;
        }

        // First, try to find in the provided gradingProfiles (for homeroom students)
        const profileInList = gradingProfiles?.find((gp) =>
            profileMatchesStudentInSemester(gp, firstSemester.key, student.studentNumber)
        );

        if (profileInList) {
            setPreviousSemesterProfile(profileInList);
            return;
        }

        // If not found (shared student case), fetch using the shared group creator's GUID
        const creatorGuid = sharedGroup?.sharedGroupCreatorGuid;
        if (!creatorGuid) {
            setPreviousSemesterProfile(null);
            return;
        }

        let isCancelled = false;

        const fetchCreatorProfiles = async () => {
            try {
                const res = await ElementaryProgressReportDao({
                    action: "getGradingProfile",
                    guid: creatorGuid,
                    schoolYearKey: schoolYearDto.key,
                    token
                });

                if (isCancelled) return;

                const profiles = res?.data?.payload || [];

                // Find semester 1 profile that contains this student
                const semester1Profile = profiles.find((gp) =>
                    profileMatchesStudentInSemester(gp, firstSemester.key, student.studentNumber)
                );

                setPreviousSemesterProfile(semester1Profile || null);
            } catch (error) {
                if (isCancelled) return;
                console.error("Error fetching semester 1 profile for shared student:", error);
                setPreviousSemesterProfile(null);
            }
        };

        fetchCreatorProfiles();

        return () => {
            isCancelled = true;
        };
    }, [isSecondSemester, student, timePeriodDto, gradingProfiles, schoolYearDto, sharedGroup, token]);

    // Get the semester 1 template for matching
    const semester1Template = useMemo(() => {
        // Determine which template to use based on student's location
        let templateDto = schoolLevelTemplateDto;

        // If student is shared from a different school, use their school's template
        if (sharedGroup?.locationKey && schoolTemplatesByLocation) {
            const studentSchoolTemplate = schoolTemplatesByLocation[sharedGroup.locationKey];
            if (studentSchoolTemplate) {
                templateDto = studentSchoolTemplate;
            }
        }

        return findSemester1Template(templateDto, previousSemesterProfile, selectedTemplate);
    }, [schoolLevelTemplateDto, schoolTemplatesByLocation, sharedGroup, previousSemesterProfile, selectedTemplate]);

    // Build content items from semester 1 template
    useEffect(() => {
        if (!semester1Template) {
            setPreviousSemesterContentItems([]);
            return;
        }

        const items = buildContentItemsFromTemplate(semester1Template);
        setPreviousSemesterContentItems(items);
    }, [semester1Template]);

    const previousSemesterCriteriaId = useMemo(() => {
        if (!previousSemesterProfile || !selectedTemplate) return null;

        // Determine which template to use based on student's location
        let templateDto = schoolLevelTemplateDto;

        // If student is shared from a different school, use their school's template
        if (sharedGroup?.locationKey && schoolTemplatesByLocation) {
            const studentSchoolTemplate = schoolTemplatesByLocation[sharedGroup.locationKey];
            if (studentSchoolTemplate) {
                templateDto = studentSchoolTemplate;
            }
        }

        const semester1Template = findSemester1Template(templateDto, previousSemesterProfile, selectedTemplate);

        if (!semester1Template) return null;

        const criteria = previousSemesterProfile.gradingCriteriaList?.find(
            (c) => Number(c.parentTemplate) === Number(semester1Template.templateId)
        );

        return criteria?.key || null;
    }, [previousSemesterProfile, selectedTemplate, schoolLevelTemplateDto, schoolTemplatesByLocation, sharedGroup]);

    // Find the student's ID in the previous semester profile
    const previousSemesterStudentId = useMemo(() => {
        if (!previousSemesterProfile || !student) return null;

        const studentInProfile = previousSemesterProfile.gradingProfileStudents?.find(
            (s) => String(s.studentNumber) === String(student.studentNumber)
        );

        return studentInProfile?.key || null;
    }, [previousSemesterProfile, student]);

    // Fetch previous semester ratings
    useEffect(() => {
        if (!isSecondSemester || !previousSemesterCriteriaId || !previousSemesterStudentId || !token) {
            setPreviousSemesterRatings(null);
            return;
        }

        let isCancelled = false;
        (async () => {
            try {
                const res = await ElementaryProgressReportDao({
                    action: "getStudentRatings",
                    gradingProfileStudentId: previousSemesterStudentId,
                    gradingCriteriaId: previousSemesterCriteriaId,
                    token
                });

                if (isCancelled) return;

                const payload = res?.data?.payload || null;
                setPreviousSemesterRatings(payload);
            } catch (error) {
                if (isCancelled) return;
                console.error("Error fetching previous semester ratings:", error);
                setPreviousSemesterRatings(null);
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [isSecondSemester, previousSemesterCriteriaId, previousSemesterStudentId, token]);

    // Fetch previous semester SLE ratings
    useEffect(() => {
        if (!isSecondSemester || !previousSemesterStudentId || !token) {
            setPreviousSemesterSleRatings([]);
            return;
        }

        let isCancelled = false;
        (async () => {
            try {
                const res = await ElementaryProgressReportDao({
                    action: "getSleRatings",
                    gradingProfileStudentId: previousSemesterStudentId,
                    token
                });

                if (isCancelled) return;

                const payload = res?.data?.payload || [];
                setPreviousSemesterSleRatings(payload);
            } catch (error) {
                if (isCancelled) return;
                console.error("Error fetching previous semester SLE ratings:", error);
                setPreviousSemesterSleRatings([]);
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [isSecondSemester, previousSemesterStudentId, token]);

    // Create mapping for previous semester diff ratings by contentId
    const previousDiffRatingsByContentId = useMemo(() => {
        if (!previousSemesterRatings?.diffRatings?.length) {
            return {};
        }

        const map = {};
        for (const diffRating of previousSemesterRatings.diffRatings) {
            const key = `${diffRating.contentId}:${diffRating.grade}`;
            map[key] = {
                rating: normalizeRating(diffRating.rating),
                comment: diffRating.comment || null
            };
        }

        return map;
    }, [previousSemesterRatings]);

    const previousRatingsByBodyText = useMemo(() => {
        if (!previousSemesterRatings?.contentRatings || !previousSemesterContentItems.length) {
            return {};
        }

        const map = {};
        const ratingsById = {};

        for (const rating of previousSemesterRatings.contentRatings) {
            ratingsById[rating.templateContentId] = {
                rating: normalizeRating(rating.rating),
                comment: rating.comment || null
            };
        }

        for (const item of previousSemesterContentItems) {
            const ratingData = ratingsById[item.templateContentId];
            if (ratingData) {
                const normalizedBody = stripHtml(item.body);
                const key = `${item.contentType}:${normalizedBody}`;
                map[key] = ratingData;
            }
        }

        return map;
    }, [previousSemesterRatings, previousSemesterContentItems]);

    const previousSleRatingsByKey = useMemo(() => {
        if (!previousSemesterSleRatings.length) {
            return {};
        }

        const map = {};
        for (const r of previousSemesterSleRatings) {
            map[`SLE:${r.sleId}`] = r.rating === "DOES_NOT_MEET" ? "DOES NOT MEET" : r.rating || null;
            for (const c of r.sleChildRatings || []) {
                map[`SLEC:${c.sleChildId}`] = c.rating === "DOES_NOT_MEET" ? "DOES NOT MEET" : c.rating || null;
            }
        }

        return map;
    }, [previousSemesterSleRatings]);

    return {
        previousRatingsByBodyText,
        previousDiffRatingsByContentId,
        previousSleRatingsByKey,
        hasPreviousSemester: isSecondSemester && !!previousSemesterProfile
    };
};

export default usePreviousSemesterRatings;
